import { formatRut, RutFormat, validateRut } from "@fdograph/rut-utilities";
import { Block, CheckCircle, HourglassEmpty } from "@material-ui/icons";

// ============================================
// 🎨 CONFIGURACIONES DE ESTADOS
// ============================================

/**
 * Configuración visual para estados de movimientos bancarios
 */
export const ESTADOS_MOVIMIENTO = {
  PENDIENTE: {
    label: "Pendiente",
    color: "warning",
    icon: <HourglassEmpty sx={{ fontSize: 16 }} />,
    description: "Sin conciliar",
  },
  CONCILIADO: {
    label: "Conciliado",
    color: "success",
    icon: <CheckCircle sx={{ fontSize: 16 }} />,
    description: "Asociado a factura(s)",
  },
  IGNORADO: {
    label: "Ignorado",
    color: "default",
    icon: <Block sx={{ fontSize: 16 }} />,
    description: "Rechazado/No aplica",
  },
};

/**
 * Obtiene configuración de estado de movimiento
 * @param {string} estado - PENDIENTE | CONCILIADO | IGNORADO
 * @returns {Object} Configuración del estado
 */
export const getEstadoMovimientoConfig = (estado) => {
  return ESTADOS_MOVIMIENTO[estado] || ESTADOS_MOVIMIENTO.PENDIENTE;
};

// ============================================
// 🏦 CONFIGURACIONES DE BANCOS
// ============================================

/**
 * Configuración visual para bancos
 */
export const BANCOS = {
  SANTANDER: {
    label: "Santander",
    color: "#EC0000",
    bgColor: "#FFF5F5",
    icon: "🏦",
    codigo: "SANTANDER",
  },
  BCI: {
    label: "BCI",
    color: "#003DA5",
    bgColor: "#F0F4FF",
    icon: "🏦",
    codigo: "BCI",
  },
  ITAU: {
    label: "Itaú",
    color: "#F37021",
    bgColor: "#FFF4ED",
    icon: "🏦",
    codigo: "ITAU",
  },
};

/**
 * Obtiene configuración de banco
 * @param {string} banco - SANTANDER | BCI
 * @returns {Object} Configuración del banco
 */
export const getBancoConfig = (banco) => {
  return (
    BANCOS[banco?.toUpperCase()] || {
      label: banco,
      color: "#666",
      bgColor: "#F5F5F5",
      icon: "🏦",
      codigo: banco,
    }
  );
};

// ============================================
// 💯 CONFIGURACIONES DE SCORING
// ============================================

/**
 * Configuración visual para scores de matching
 */
export const getScoreConfig = (score) => {
  if (score >= 90) {
    return {
      label: "Alto",
      color: "success",
      autoConcilar: true,
      description: "Conciliación automática",
      badgeColor: "#4CAF50",
      score,
    };
  }
  if (score >= 70) {
    return {
      label: "Medio",
      color: "warning",
      autoConcilar: false,
      description: "Revisar sugerencia",
      badgeColor: "#FF9800",
      score,
    };
  }
  return {
    label: "Bajo",
    color: "error",
    autoConcilar: false,
    description: "Conciliación manual",
    badgeColor: "#F44336",
    score,
  };
};

// ============================================
// 💰 FORMATEO DE MONTOS
// ============================================

/**
 * Formatea un monto en formato chileno (CLP)
 * @param {number|string} monto - Monto a formatear
 * @returns {string} Monto formateado (ej: "$1.234.567")
 */
export const formatearMonto = (monto) => {
  const numero = parseFloat(monto) || 0;
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numero);
};

/**
 * Formatea un monto sin el símbolo de peso
 * @param {number|string} monto - Monto a formatear
 * @returns {string} Monto formateado (ej: "1.234.567")
 */
export const formatearMontoSinSimbolo = (monto) => {
  const numero = parseFloat(monto) || 0;
  return new Intl.NumberFormat("es-CL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numero);
};

// ============================================
// 🆔 FORMATEO DE RUTs
// ============================================

/**
 * Formatea un RUT para display (con puntos y guión)
 * @param {string} rut - RUT sin formato (ej: "764692071")
 * @returns {string} RUT formateado (ej: "76.469.207-1")
 */
export const formatearRutDisplay = (rut) => {
  if (!rut) return "S/I";

  try {
    // Si el RUT ya viene formateado, retornarlo
    if (rut.includes("-")) {
      return formatRut(rut, RutFormat.DOTS_DASH);
    }

    // Si viene sin DV (ej: "76469207"), agregarlo
    if (rut.length <= 8) {
      return formatRut(rut, RutFormat.DOTS_DASH);
    }

    // Si viene con DV pero sin formato (ej: "764692071")
    // Separar número y DV
    const numero = rut.slice(0, -1);
    const dv = rut.slice(-1);
    return formatRut(`${numero}-${dv}`, RutFormat.DOTS_DASH);
  } catch (error) {
    return rut;
  }
};

/**
 * Valida un RUT chileno
 * @param {string} rut - RUT a validar
 * @returns {boolean} True si es válido
 */
export const esRutValido = (rut) => {
  if (!rut) return false;
  return validateRut(rut);
};

// ============================================
// 🔢 CÁLCULOS Y VALIDACIONES
// ============================================

/**
 * Calcula totales de una lista de movimientos
 * @param {Array} movimientos - Lista de movimientos
 * @returns {Object} Objeto con totales
 */
export const calcularTotales = (movimientos) => {
  if (!Array.isArray(movimientos) || movimientos.length === 0) {
    return {
      total: 0,
      pendientes: 0,
      conciliados: 0,
      ignorados: 0,
      montoPendiente: 0,
      montoConciliado: 0,
    };
  }

  return movimientos.reduce(
    (acc, mov) => {
      const monto = parseFloat(mov.monto) || 0;

      acc.total++;

      switch (mov.estado) {
        case "PENDIENTE":
          acc.pendientes++;
          acc.montoPendiente += monto;
          break;
        case "CONCILIADO":
          acc.conciliados++;
          acc.montoConciliado += monto;
          break;
        case "IGNORADO":
          acc.ignorados++;
          break;
        default:
          break;
      }

      return acc;
    },
    {
      total: 0,
      pendientes: 0,
      conciliados: 0,
      ignorados: 0,
      montoPendiente: 0,
      montoConciliado: 0,
    },
  );
};

/**
 * Valida si un movimiento puede ser revertido
 * @param {Object} movimiento - Objeto movimiento
 * @returns {boolean} True si puede revertirse
 */
export const puedeRevertir = (movimiento) => {
  return movimiento?.estado === "CONCILIADO";
};

/**
 * Valida si un movimiento puede ser conciliado
 * @param {Object} movimiento - Objeto movimiento
 * @returns {boolean} True si puede conciliarse
 */
export const puedeConciliar = (movimiento) => {
  return movimiento?.estado === "PENDIENTE";
};

/**
 * Valida si un movimiento puede ser rechazado
 * @param {Object} movimiento - Objeto movimiento
 * @returns {boolean} True si puede rechazarse
 */
export const puedeRechazar = (movimiento) => {
  return movimiento?.estado === "PENDIENTE";
};

// ============================================
// 📊 HELPERS DE CONCILIACIÓN
// ============================================

/**
 * Calcula la suma total de montos de un array de facturas
 * @param {Array} facturas - Array de facturas con campo 'monto'
 * @returns {number} Suma total de los montos
 */
export const calcularSumaFacturas = (facturas) => {
  if (!Array.isArray(facturas) || facturas.length === 0) {
    return 0;
  }

  return facturas.reduce((suma, factura) => {
    const monto = parseFloat(factura?.monto) || 0;
    return suma + monto;
  }, 0);
};

/**
 * Valida que la suma de montos parciales no exceda el total
 * @param {Array} facturas - Lista de facturas con montos asignados
 * @param {number} montoTotal - Monto total del movimiento
 * @returns {Object} { valido: boolean, diferencia: number }
 */
export const validarMontosParciales = (facturas, montoTotal) => {
  const sumaAsignada = facturas.reduce(
    (sum, f) => sum + (parseFloat(f.monto_asignado) || 0),
    0,
  );

  const diferencia = montoTotal - sumaAsignada;

  return {
    valido: diferencia >= 0,
    diferencia,
    sumaAsignada,
    montoTotal,
    porcentaje: (sumaAsignada / montoTotal) * 100,
  };
};

// ============================================
// ⚖️ NATURALEZA DE UNA LÍNEA DE CATEGORÍA
// ============================================

/**
 * Naturalezas posibles de una línea de categoría contable dentro de una
 * conciliación: si su monto entró junto con el movimiento o si se retuvo
 * antes de depositarlo.
 *
 * El signo es propiedad de la línea y no de la categoría. La misma categoría
 * puede aparecer sumando en un movimiento y restando en otro, así que no se
 * deduce del catálogo ni de comparar la suma de facturas contra el monto.
 */
export const NATURALEZA_LINEA = {
  ADITIVA: "ADITIVA",
  SUSTRACTIVA: "SUSTRACTIVA",
};

/**
 * Opciones del selector de naturaleza, en el orden en que se ofrecen.
 * Las etiquetas describen el hecho bancario, no la aritmética: el operador
 * sabe si el dinero llegó con el depósito o si se lo descontaron antes.
 */
export const OPCIONES_NATURALEZA_LINEA = [
  { value: NATURALEZA_LINEA.ADITIVA, label: "Ingresó con el pago" },
  {
    value: NATURALEZA_LINEA.SUSTRACTIVA,
    label: "Retenido antes del depósito",
  },
];

/**
 * Etiqueta legible de una naturaleza
 * @param {string} naturaleza - ADITIVA | SUSTRACTIVA
 * @returns {string} Texto que ve el operador
 */
export const etiquetaNaturaleza = (naturaleza) =>
  OPCIONES_NATURALEZA_LINEA.find((o) => o.value === naturaleza)?.label ||
  OPCIONES_NATURALEZA_LINEA[0].label;

/**
 * Margen en pesos dentro del cual un remanente se considera cuadre exacto.
 * Es el mismo que aplica el backend al decidir si genera abono: si acá fuera
 * distinto, el desglose diría "cuadra" sobre un reparto que termina abonando,
 * o al revés.
 */
export const TOLERANCIA_REPARTO = 1;

/**
 * Resuelve la naturaleza efectiva de una línea de categoría. Único lugar
 * donde se decide qué significa que el campo no venga.
 *
 * La AUSENCIA del campo se lee como aditiva, que es lo que hacía el sistema
 * antes de que el signo existiera por línea. Un null no es ausencia: es un
 * valor que alguien escribió, casi siempre por un defecto aguas arriba, y
 * leerlo como ausencia lo silenciaría.
 *
 * @param {Object} linea - Línea de categoría ({ monto, naturaleza })
 * @returns {string} ADITIVA | SUSTRACTIVA
 */
export const naturalezaDeLinea = (linea) =>
  linea?.naturaleza === undefined ? NATURALEZA_LINEA.ADITIVA : linea.naturaleza;

/**
 * Clasifica una línea de categoría por su naturaleza
 * @param {Object} linea - Línea de categoría ({ monto, naturaleza })
 * @returns {boolean} True si el monto se retuvo antes del depósito
 */
export const esLineaSustractiva = (linea) =>
  naturalezaDeLinea(linea) === NATURALEZA_LINEA.SUSTRACTIVA;

/**
 * Reparte el monto de un movimiento entre sus facturas, sus líneas de
 * categoría y el remanente, con la misma fórmula que aplica el backend:
 *
 *   remanente = movimiento − facturas − Σ aditivas + Σ sustractivas
 *
 * Quien decide si un reparto se acepta es el backend; acá el cálculo existe
 * solo para mostrarle al operador de dónde sale cada cifra antes de enviar.
 *
 * @param {Object} params
 * @param {number} params.montoMovimiento - Monto del movimiento bancario
 * @param {number} params.sumaFacturas - Suma de los montos asignados a facturas
 * @param {Array} params.lineas - Líneas de categoría ({ monto, naturaleza })
 * @returns {Object} Subtotales por naturaleza, total a repartir y remanente
 */
export const calcularRepartoCategorias = ({
  montoMovimiento,
  sumaFacturas = 0,
  lineas = [],
}) => {
  const listaLineas = Array.isArray(lineas) ? lineas : [];

  const lineasSustractivas = listaLineas.filter(esLineaSustractiva);
  const lineasAditivas = listaLineas.filter((l) => !esLineaSustractiva(l));

  const sumarMontos = (total, linea) => total + (parseFloat(linea?.monto) || 0);
  const sumaAditivas = lineasAditivas.reduce(sumarMontos, 0);
  const sumaSustractivas = lineasSustractivas.reduce(sumarMontos, 0);

  const monto = parseFloat(montoMovimiento) || 0;
  const facturas = parseFloat(sumaFacturas) || 0;

  return {
    lineasAditivas,
    lineasSustractivas,
    sumaAditivas,
    sumaSustractivas,
    // Lo depositado más lo que se retuvo antes de depositarlo: el dinero que
    // el reparto tiene que explicar entero.
    totalARepartir: monto + sumaSustractivas,
    remanente: monto - facturas - sumaAditivas + sumaSustractivas,
  };
};

/**
 * Genera texto descriptivo de razones de matching
 * @param {Array} razones - Array de razones
 * @returns {string} Texto formateado
 */
export const formatearRazonesMatching = (razones) => {
  if (!Array.isArray(razones) || razones.length === 0) {
    return "Sin razones";
  }
  return razones.join(" • ");
};

// ============================================
// 🎯 HELPERS DE UI
// ============================================

/**
 * Genera opciones para select de bancos
 * @returns {Array} Array de opciones
 */
export const getOpcionesBancos = () => {
  return [
    { value: "", label: "Todos los bancos" },
    { value: "SANTANDER", label: "🏦 Santander" },
    { value: "BCI", label: "🏦 BCI" },
    { value: "ITAU", label: "🏦 Itaú" },
  ];
};

/**
 * Genera opciones para select de estados
 * @returns {Array} Array de opciones
 */
export const getOpcionesEstados = () => {
  return [
    { value: "", label: "Todos los estados" },
    { value: "PENDIENTE", label: "⏳ Pendiente" },
    { value: "CONCILIADO", label: "✅ Conciliado" },
    { value: "IGNORADO", label: "🚫 Ignorado" },
  ];
};

/**
 * Trunca texto largo con ellipsis
 * @param {string} texto - Texto a truncar
 * @param {number} maxLength - Longitud máxima
 * @returns {string} Texto truncado
 */
export const truncarTexto = (texto, maxLength = 50) => {
  if (!texto) return "";
  if (texto.length <= maxLength) return texto;
  return `${texto.substring(0, maxLength)}...`;
};

/**
 * Formatea el período del lote
 * @param {string} periodoDesde - Fecha desde (ISO)
 * @param {string} periodoHasta - Fecha hasta (ISO)
 * @returns {string} Período formateado (ej: "Sep 2025" o "22-30 Sep 2025")
 */
export const formatearPeriodoLote = (periodoDesde, periodoHasta) => {
  if (!periodoDesde) return "S/P";

  const fechaDesde = new Date(periodoDesde);
  const mesNombre = fechaDesde.toLocaleDateString("es-CL", {
    month: "short",
    year: "numeric",
  });

  // Si no hay fecha hasta o es inválida, mostrar solo mes/año
  if (!periodoHasta || periodoHasta === "0000-00-00") {
    return mesNombre.charAt(0).toUpperCase() + mesNombre.slice(1);
  }

  // Si hay rango, mostrar días + mes
  const fechaHasta = new Date(periodoHasta);
  const diaDesde = fechaDesde.getDate();
  const diaHasta = fechaHasta.getDate();

  return `${diaDesde}-${diaHasta} ${mesNombre}`.replace(".", "");
};

// ============================================
// CONFIGURACIÓN DE BANCOS DISPONIBLES
// ============================================

/**
 * Lista de bancos configurados en el sistema
 * Valores deben coincidir con banco_config.banco en BD
 */
export const BANCOS_DISPONIBLES = [
  {
    value: "SANTANDER",
    label: "Banco Santander",
    icon: "🔴",
  },
  {
    value: "BCI",
    label: "Banco BCI",
    icon: "🔵",
  },
  {
    value: "ITAU",
    label: "Banco Itaú",
    icon: "🟠",
  },
];

/**
 * Detecta el banco basándose en el nombre del archivo
 * Réplica de la lógica del backend para UX consistente
 *
 * @param {string} filename - Nombre del archivo
 * @returns {string|null} - Código del banco ("SANTANDER", "BCI") o null
 */
export const detectarBancoPorNombre = (filename) => {
  if (!filename) return null;

  const nombreLower = filename.toLowerCase();

  if (nombreLower.includes("santander")) return "SANTANDER";
  if (nombreLower.includes("bci")) return "BCI";
  if (nombreLower.includes("itau") || nombreLower.includes("itaú"))
    return "ITAU";

  return null;
};

/**
 * Obtiene la configuración visual de un banco por su código
 *
 * @param {string} codigo - Código del banco
 * @returns {Object} - Configuración del banco
 */
export const obtenerConfigBanco = (codigo) => {
  return BANCOS_DISPONIBLES.find((b) => b.value === codigo) || null;
};
